import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Alert,
} from '@mui/material';
import {
  EmojiEvents,
  CheckCircle,
  Cancel,
  Refresh,
  ArrowBack,
  Assignment,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { StandaloneExamAttempt } from '../types';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import Breadcrumb from '../components/Common/Breadcrumb';

const StandaloneExamResult: React.FC = () => {
  const { hashed, attemptId } = useParams<{ hashed: string; attemptId: string }>();
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState<StandaloneExamAttempt | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const [certificateStatus, setCertificateStatus] = useState<'pending' | 'generating' | 'ready' | 'failed' | ''>('');
  const [certificateUrl, setCertificateUrl] = useState<string>('');
  const [certificatePreviewUrl, setCertificatePreviewUrl] = useState<string>('');
  const [certificateError, setCertificateError] = useState<string>('');
  const [certificatePolling, setCertificatePolling] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const pollRequestIdRef = useRef(0);

  useEffect(() => {
    const fetchResult = async () => {
      if (!attemptId) return;
      try {
        setLoading(true);
        const data = await apiService.getStandaloneExamAttempt(parseInt(attemptId));
        setAttempt(data);
      } catch (error) {
        console.error('Error fetching result:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchResult();
  }, [attemptId]);

  // Poll certificate status until generated or failed
  useEffect(() => {
    if (!attempt) return;
    const isPassedNow = attempt.is_passed ?? false;
    const grants = attempt.standalone_exam?.grants_certificate ?? false;
    const canPoll = isPassedNow && grants;

    if (!canPoll) return;
    if (certificatePolling) return;
    if (certificateStatus === 'ready' || certificateStatus === 'failed') return;

    pollRequestIdRef.current += 1;
    const myPollId = pollRequestIdRef.current;

    const delayMs = 3000;

    let cancelled = false;

    const fetchStatus = async () => {
      try {
        const data = await apiService.getStandaloneExamAttemptCertificateStatus(attempt.id);
        if (cancelled) return;
        if (myPollId !== pollRequestIdRef.current) return;

        const statusVal = (data.status as string) || 'pending';
        setCertificateStatus(statusVal as any);
        setCertificateError((data.error_message as string) || '');
        setCertificateUrl(data.certificate_url || '');
        setCertificatePreviewUrl(data.certificate_preview_url || '');

        if (statusVal === 'ready' || statusVal === 'failed') {
          setCertificatePolling(false);
          return;
        }

        setTimeout(fetchStatus, delayMs);
      } catch (e: unknown) {
        if (cancelled) return;
        if (myPollId !== pollRequestIdRef.current) return;
        setCertificateStatus('failed');
        const ax = e as { response?: { status?: number; data?: { detail?: string } } };
        const msg =
          ax.response?.status === 401 || ax.response?.status === 403
            ? 'Sign in required to load certificate status. Refresh the page after logging in.'
            : (ax.response?.data?.detail as string) || 'We could not prepare your certificate right now.';
        setCertificateError(msg);
        setCertificatePolling(false);
      }
    };

    setCertificatePolling(true);
    setCertificateStatus('generating');
    setCertificateError('');
    fetchStatus();

    return () => {
      cancelled = true;
    };
  }, [attempt, certificatePolling, certificateStatus]);

  if (loading) return <LoadingSpinner message="Calculating your results..." />;
  if (!attempt) return <Alert severity="error">Attempt not found.</Alert>;

  const isPassed = attempt.is_passed ?? false;
  const exam = attempt.standalone_exam;

  const handlePreviewCertificate = async () => {
    if (!attempt) return;
    if (certificatePreviewUrl) {
      window.open(certificatePreviewUrl, '_blank');
      return;
    }
    const win = window.open('', '_blank');
    if (!win) {
      return;
    }
    win.document.write('<p style="font-family:sans-serif;padding:16px;">Loading certificate preview...</p>');
    win.document.close();

    apiService
      .getCertificatePreviewStandalone(attempt.id)
      .then((data) => {
        if (data.image_url) {
          win.location.href = data.image_url;
        } else if (data.html) {
          win.document.open();
          win.document.write(data.html);
          win.document.close();
        } else {
          win.document.body.innerHTML =
            '<p style="font-family:sans-serif;padding:16px;">Preview not available.</p>';
        }
      })
      .catch((error) => {
        console.error('Error loading certificate preview', error);
        win.document.body.innerHTML =
          '<p style="font-family:sans-serif;padding:16px;color:red;">Failed to load certificate preview.</p>';
      });
  };

  const handleDownloadCertificate = async () => {
    if (!attempt || !exam) return;
    if (certificateUrl) {
      window.open(certificateUrl, '_blank');
      return;
    }
    try {
      setDownloading(true);
      const safeName = (exam.title || 'exam').replace(/\s+/g, '_').slice(0, 50);
      await apiService.downloadCertificateStandalone(attempt.id, `certificate_${safeName}.pdf`);
    } catch (error) {
      console.error('Error downloading certificate', error);
    } finally {
      setDownloading(false);
    }
  };

  const handleSendCertificateEmail = async () => {
    if (!attempt) return;
    try {
      setSendingEmail(true);
      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
      const url = `${apiBase.replace(/\/$/, '')}/certificates/standalone/${attempt.id}/send-email/`;
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const msg = (data && (data.detail as string)) || `Failed with status ${response.status}`;
        alert(`Failed to send certificate email: ${msg}`);
        return;
      }
      alert('Certificate has been sent to your email.');
    } catch (e) {
      console.error('Error sending certificate email', e);
      alert('Unexpected error while sending certificate email.');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Breadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Exams', path: '/exams' },
          { label: exam?.title ?? 'Exam', path: hashed ? `/exams/${hashed}` : '/exams' },
          { label: 'Result' },
        ]}
      />

      <Box sx={{ mt: 4, textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
        <Paper
          sx={{
            p: { xs: 4, md: 6 },
            borderRadius: 4,
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            background: isPassed
              ? 'linear-gradient(135deg, #1e293b 0%, #0f766e 100%)'
              : 'linear-gradient(135deg, #1e293b 0%, #7f1d1d 100%)',
            border: `3px solid ${isPassed ? '#10b981' : '#ef4444'}`,
            maxWidth: '700px',
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: -100,
              right: -100,
              width: 250,
              height: 250,
              borderRadius: '50%',
              background: isPassed
                ? 'radial-gradient(circle, rgba(16, 185, 129, 0.25) 0%, transparent 70%)'
                : 'radial-gradient(circle, rgba(239, 68, 68, 0.25) 0%, transparent 70%)',
              zIndex: 0,
            }}
          />
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box
              sx={{
                display: 'inline-flex',
                p: 2.5,
                borderRadius: '50%',
                background: isPassed
                  ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.3) 0%, rgba(16, 185, 129, 0.15) 100%)'
                  : 'linear-gradient(135deg, rgba(239, 68, 68, 0.3) 0%, rgba(239, 68, 68, 0.15) 100%)',
                border: `3px solid ${isPassed ? '#10b981' : '#ef4444'}`,
                mb: 3,
              }}
            >
              {isPassed ? (
                <EmojiEvents sx={{ fontSize: '4rem', color: '#fbbf24' }} />
              ) : (
                <Cancel sx={{ fontSize: '4rem', color: '#fca5a5' }} />
              )}
            </Box>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                mb: 2,
                color: isPassed ? '#6ee7b7' : '#fca5a5',
                fontSize: { xs: '1.75rem', md: '2.5rem' },
              }}
            >
              {isPassed ? 'Congratulations!' : 'Keep Trying!'}
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: '#cbd5e1', fontWeight: 500 }}>
              {isPassed
                ? `You have successfully passed ${exam?.title ?? 'the exam'}!`
                : `You didn't reach the passing score for ${exam?.title ?? 'this exam'}.`}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: { xs: 3, md: 4 },
                mb: 4,
                flexWrap: 'wrap',
              }}
            >
              <Box
                sx={{
                  p: 3,
                  borderRadius: 3,
                  background: isPassed
                    ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.25) 0%, rgba(16, 185, 129, 0.15) 100%)'
                    : 'linear-gradient(135deg, rgba(239, 68, 68, 0.25) 0%, rgba(239, 68, 68, 0.15) 100%)',
                  minWidth: { xs: '130px', md: '160px' },
                  border: `3px solid ${isPassed ? '#10b981' : '#ef4444'}`,
                }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 900,
                    color: isPassed ? '#6ee7b7' : '#fca5a5',
                    fontSize: { xs: '2.5rem', md: '3rem' },
                    lineHeight: 1,
                  }}
                >
                  {attempt.score}%
                </Typography>
                <Typography variant="body2" sx={{ color: '#a7f3d0', fontWeight: 700, mt: 1 }}>
                  Your Score
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 3,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, rgba(148, 163, 184, 0.2) 0%, rgba(148, 163, 184, 0.1) 100%)',
                  minWidth: { xs: '130px', md: '160px' },
                  border: '3px solid #64748b',
                }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 900,
                    color: '#cbd5e1',
                    fontSize: { xs: '2.5rem', md: '3rem' },
                    lineHeight: 1,
                  }}
                >
                  {exam?.passing_score ?? 0}%
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 700, mt: 1 }}>
                  Required
                </Typography>
              </Box>
            </Box>
            <Alert
              severity={isPassed ? 'success' : 'error'}
              icon={isPassed ? <CheckCircle /> : <Cancel />}
              sx={{
                mb: 4,
                borderRadius: 2,
                fontWeight: 700,
                justifyContent: 'center',
                py: 1.5,
                px: 3,
              }}
            >
              {isPassed ? 'PASSED ✓' : 'NOT PASSED ✗'}
            </Alert>

            {isPassed && exam?.grants_certificate && (
              <Typography
                variant="body2"
                sx={{
                  mb: 3,
                  color: '#e5e7eb',
                  fontWeight: 500,
                }}
              >
                {certificateStatus === 'failed'
                  ? certificateError || 'We could not generate your certificate automatically. Please contact support.'
                  : 'Your certificate is ready.'}
              </Typography>
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2 }}>
              <Button
                variant="outlined"
                size="large"
                startIcon={<ArrowBack />}
                onClick={() => navigate('/exams')}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1.25,
                  fontWeight: 600,
                  borderWidth: 2,
                  borderColor: '#94a3b8',
                  color: '#cbd5e1',
                  '&:hover': {
                    backgroundColor: 'rgba(148, 163, 184, 0.2)',
                    borderColor: '#cbd5e1',
                  },
                }}
              >
                Back to Exams
              </Button>
              {!isPassed && hashed && (
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Refresh />}
                  onClick={() => navigate(`/exams/${hashed}/take`)}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1.25,
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                    color: '#064e3b',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                    },
                  }}
                >
                  Try Again
                </Button>
              )}
              {hashed && (
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Assignment />}
                  onClick={() => navigate(`/exams/${hashed}`)}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1.25,
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                    color: '#064e3b',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                    },
                  }}
                >
                  View Exam
                </Button>
              )}
              {isPassed && exam?.grants_certificate && (
                <>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={handlePreviewCertificate}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1.25,
                      fontWeight: 600,
                      borderWidth: 2,
                      borderColor: '#10b981',
                      color: '#bbf7d0',
                      '&:hover': {
                        backgroundColor: 'rgba(16, 185, 129, 0.15)',
                        borderColor: '#22c55e',
                      },
                    }}
                  >
                    Preview Certificate
                  </Button>

                  <Button
                    variant="contained"
                    size="large"
                    disabled={downloading}
                    onClick={handleDownloadCertificate}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1.25,
                      fontWeight: 700,
                      background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                      color: '#064e3b',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                      },
                    }}
                  >
                    {downloading ? 'Downloading...' : 'Download Certificate'}
                  </Button>

                  <Button
                    variant="outlined"
                    size="large"
                    disabled={sendingEmail}
                    onClick={handleSendCertificateEmail}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1.25,
                      fontWeight: 600,
                      borderWidth: 2,
                      borderColor: '#38bdf8',
                      color: '#e0f2fe',
                      '&:hover': {
                        backgroundColor: 'rgba(56, 189, 248, 0.15)',
                        borderColor: '#0ea5e9',
                      },
                    }}
                  >
                    {sendingEmail ? 'Sending...' : 'Send Certificate via Email'}
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Paper>
      </Box>
      <Box sx={{ mt: 5, textAlign: 'center' }}>
        {isPassed && exam?.grants_certificate && (
          <Alert
            severity="info"
            sx={{
              mb: 3,
              mx: 'auto',
              maxWidth: '600px',
              borderRadius: 2,
            }}
          >
            Congratulations! Your certificate will be sent to you via email if applicable.
          </Alert>
        )}
      </Box>
    </Container>
  );
};

export default StandaloneExamResult;
